import React, { useState } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../components/ui/sheet";
import { Headphones, Sparkles, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "bug", label: "Bug" },
  { value: "billing", label: "Billing" },
  { value: "content", label: "Content question" },
  { value: "feature", label: "Feature request" },
  { value: "other", label: "Other" },
];

export default function ContactSupportSheet({ open, onOpenChange }) {
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("other");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const reset = () => { setSubject(""); setCategory("other"); setMessage(""); setCreated(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) {
      toast.error("Subject and message are required");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/support/tickets", {
        subject: subject.trim(),
        message: message.trim(),
        category,
      });
      setCreated(r.data);
      toast.success("Message sent — we'll be in touch");
    } catch (err) {
      console.error("Support ticket failed", err);
      toast.error(err.response?.data?.detail || "Failed to send ticket");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setTimeout(reset, 300); }}>
      <SheetContent side="right" className="bg-white border-l border-gray-200 text-white w-full sm:max-w-[520px] overflow-y-auto" data-testid="support-sheet">
        <SheetHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-fc-red/15 text-fc-red flex items-center justify-center">
              <Headphones size={20} />
            </div>
            <div>
              <SheetTitle className="text-white font-black tracking-tight text-xl">Contact Support</SheetTitle>
              <SheetDescription className="text-gray-500 text-xs tracking-[0.18em] uppercase">
                We reply within 1 business day
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {created ? (
          <div className="mt-8 space-y-5" data-testid="support-success">
            <div className="bg-fc-green/10 border border-fc-green/40 p-5 flex items-start gap-3">
              <CheckCircle2 className="text-[#10B981] flex-shrink-0 mt-0.5" size={22} />
              <div>
                <p className="font-black text-base">Ticket received</p>
                <p className="text-gray-500 text-sm mt-1 leading-relaxed">
                  Reference <span className="font-mono text-fc-red font-bold">#{created.ticket_id?.slice(0, 8)}</span>.
                  A human will reply to your account email shortly.
                </p>
              </div>
            </div>
            <button
              onClick={() => { reset(); }}
              className="text-xs tracking-[0.18em] uppercase font-bold text-fc-red hover:underline"
              data-testid="support-new-ticket"
            >
              Send another →
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-5" data-testid="support-form">
            <div>
              <label className="text-[10px] tracking-[0.22em] uppercase font-bold text-gray-500 block mb-2">Subject</label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Short summary"
                className="w-full bg-white border border-gray-200 text-white px-3 py-2.5 text-sm focus:outline-none focus:border-fc-red"
                data-testid="support-subject"
                maxLength={200}
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.22em] uppercase font-bold text-gray-500 block mb-2">Category</label>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map(c => (
                  <button
                    type="button"
                    key={c.value}
                    onClick={() => setCategory(c.value)}
                    className={`px-3 py-1.5 text-[10px] tracking-[0.18em] uppercase font-bold border ${category===c.value ? "border-fc-red bg-fc-red text-white" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}
                    data-testid={`support-cat-${c.value}`}
                  >{c.label}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.22em] uppercase font-bold text-gray-500 block mb-2">Message</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us what's going on. Include URLs, error messages, screenshots references."
                rows={8}
                className="w-full bg-white border border-gray-200 text-white px-3 py-2.5 text-sm focus:outline-none focus:border-fc-red resize-none"
                data-testid="support-message"
                maxLength={8000}
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="btn-fc-red w-full justify-center"
              data-testid="support-submit"
            >
              {busy ? "Sending…" : "Send to Support"}
            </button>
            <p className="text-[10px] tracking-wider text-gray-400 text-center">
              <Sparkles size={10} className="inline mr-1" /> Looking to build something? Use the &quot;Talk to us&quot; link in the sidebar.
            </p>
          </form>
        )}
      </SheetContent>
    </Sheet>
  );
}
